import { PIAPI } from '@core/base';
import { defu } from 'defu';
import { CreateTaskRequest, TaskResponse } from '@core/types';
import { Kling } from './types';

export class KlingAPI extends PIAPI {
    private config: Kling.Config;

    constructor(apiKey: string, config: Kling.Config = {}, baseUrl?: string) {
        super(apiKey, baseUrl);
        this.config = config;
    }

    private static DEFAULT_PARAMS = {
        duration: 5 as Kling.Duration,
        aspect_ratio: '16:9' as Kling.AspectRatio,
        mode: "std" as Kling.Mode,
        version: "1.0" as Kling.Version,
        cfg_scale: 0.5,
    };

    private static DEFAULT_CAMERA: Kling.CameraControl = {
        type: "default",
        config: {
            horizontal: 0,
            pan: 0,
            roll: 0,
            tilt: 0,
            vertical: 0,
            zoom: 0,
        },
    };

    private createKlingRequest(
        params: Kling.TextToVideoParams | Kling.ImageToVideoParams | Kling.ElementsToVideoParams
    ): CreateTaskRequest {
        this.logger.debug('Creating Kling request with params:', params);

        const finalInput = defu(params, KlingAPI.DEFAULT_PARAMS);

        if (![5, 10].includes(finalInput.duration)) {
            const error = "Kling duration must be either 5 or 10 seconds";
            this.logger.error(error, { duration: finalInput.duration });
            throw new Error(error);
        }

        const request: CreateTaskRequest = {
            model: "kling",
            task_type: "video_generation",
            input: finalInput,
        };

        // 只有当配置了 webhook 相关信息时才添加 webhook 配置
        if (this.config.webhookUrl && this.config.webhookSecret) {
            request.config = {
                webhook_config: {
                    endpoint: this.config.webhookUrl,
                    secret: this.config.webhookSecret,
                },
            };
        }

        return request;
    }

    async textToVideo(params: Kling.TextToVideoParams): Promise<TaskResponse<Kling.TextToVideoOutput>> {
        this.logger.info('Starting text-to-video generation', { prompt: params.prompt });
        const request = this.createKlingRequest(params);
        return this.createTask<Kling.TextToVideoOutput>(request);
    }

    async imageToVideo(params: Kling.ImageToVideoParams): Promise<TaskResponse<Kling.ImageToVideoOutput>> {
        const request = this.createKlingRequest(params);
        return this.createTask<Kling.ImageToVideoOutput>(request);
    }

    async elementsToVideo(params: Kling.ElementsToVideoParams): Promise<TaskResponse<Kling.ImageToVideoOutput>> {
        if (!params.elements || !Array.isArray(params.elements) || params.elements.length === 0) {
            throw new Error("Elements array is required and must not be empty");
        }

        if (params.elements.length > 4) {
            throw new Error("Maximum 4 elements are allowed");
        }

        const request = this.createKlingRequest(params);
        return this.createTask<Kling.ImageToVideoOutput>(request);
    }

    // 分别为不同类型的任务提供状态查询方法
    async getTextToVideoTask(taskId: string): Promise<TaskResponse<Kling.TextToVideoOutput>> {
        return this.getTask<Kling.TextToVideoOutput>(taskId);
    }

    async getImageToVideoTask(taskId: string): Promise<TaskResponse<Kling.ImageToVideoOutput>> {
        return this.getTask<Kling.ImageToVideoOutput>(taskId);
    }

    async getElementsToVideoTask(taskId: string): Promise<TaskResponse<Kling.ImageToVideoOutput>> {
        return this.getTask<Kling.ImageToVideoOutput>(taskId);
    }
} 